import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Save } from 'lucide-react';
import logger from '@/utils/logger';

const ParentFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  parent = null,
  mode = 'create', // 'create' or 'edit'
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    // Informations personnelles
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'male',
    parentId: '',

    // Informations de contact
    phone: '',
    email: '',
    secondaryPhone: '',
    emergencyContactName: '',
    emergencyContact: '',

    // Adresse
    address: {
      street: '',
      city: 'Rabat',
      postalCode: '',
      country: 'Maroc',
      latitude: '',
      longitude: '',
    },

    // Informations familiales
    relationToChild: 'father',

    // Préférences
    preferredContact: 'phone',
    language: 'french',

    // Statut
    status: 'active',
  });

  const [errors, setErrors] = useState({});

  // Pré-remplir le formulaire en mode édition
  useEffect(() => {
    logger.debug('🔄 [ParentFormModal] useEffect déclenché:', {
      isOpen,
      parent,
      mode,
    });

    if (isOpen && parent && mode === 'edit') {
      setFormData({
        firstName: parent.firstName || parent.first_name || '',
        lastName: parent.lastName || parent.last_name || '',
        dateOfBirth: parent.dateOfBirth || parent.date_of_birth || '',
        gender: parent.gender || 'male',
        parentId: parent.parentId || parent.id || '',
        phone: parent.phone || '',
        email: parent.email || '',
        secondaryPhone: parent.secondaryPhone || parent.secondary_phone || '',
        emergencyContactName: parent.emergencyContactName || parent.emergency_contact_name || '',
        emergencyContact: parent.emergencyContact || parent.emergency_contact_phone || '',
        // Mapping de l'adresse avec logs détaillés
        address: (() => {
          logger.debug('🏠 [ParentFormModal] Adresse parent reçue:', parent.address);
          logger.debug("🏠 [ParentFormModal] Type de l'adresse:", typeof parent.address);

          let addressData = {};

          if (parent.address) {
            // Si l'adresse est un objet
            if (typeof parent.address === 'object') {
              addressData = {
                street: parent.address.street || '',
                city: parent.address.city || 'Rabat',
                postalCode: parent.address.postalCode || parent.address.postal_code || '',
                country: parent.address.country || 'Maroc',
                latitude: parent.address.latitude || parent.latitude || '',
                longitude: parent.address.longitude || parent.longitude || '',
              };
            }
            // Si l'adresse est une chaîne JSON
            else if (typeof parent.address === 'string') {
              try {
                const parsedAddress = JSON.parse(parent.address);
                addressData = {
                  street: parsedAddress.street || '',
                  city: parsedAddress.city || 'Rabat',
                  postalCode: parsedAddress.postalCode || parsedAddress.postal_code || '',
                  country: parsedAddress.country || 'Maroc',
                  latitude: parsedAddress.latitude || '',
                  longitude: parsedAddress.longitude || '',
                };
              } catch (e) {
                logger.error('❌ [ParentFormModal] Erreur parsing adresse JSON:', e);
                addressData = {
                  street: '',
                  city: 'Rabat',
                  postalCode: '',
                  country: 'Maroc',
                  latitude: '',
                  longitude: '',
                };
              }
            }
          } else {
            // Fallback vers les champs individuels
            addressData = {
              street: parent.street || '',
              city: parent.city || 'Rabat',
              postalCode: parent.postal_code || '',
              country: parent.country || 'Maroc',
              latitude: parent.latitude || '',
              longitude: parent.longitude || '',
            };
          }

          logger.debug('🏠 [ParentFormModal] Adresse mappée:', addressData);
          return addressData;
        })(),
        relationToChild: parent.relationToChild || parent.relation_to_child || 'father',
        preferredContact: parent.preferredContact || parent.preferred_contact || 'phone',
        language: parent.language || 'french',
        status: parent.status || 'active',
      });

      logger.debug('✅ [ParentFormModal] FormData mis à jour pour édition');
    } else if (isOpen && mode === 'create') {
      logger.debug('🆕 [ParentFormModal] Mode création - réinitialisation du formulaire');
      setFormData({
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        gender: 'male',
        parentId: '',
        phone: '',
        email: '',
        secondaryPhone: '',
        emergencyContactName: '',
        emergencyContact: '',
        address: {
          street: '',
          city: 'Rabat',
          postalCode: '',
          country: 'Maroc',
        },
        relationToChild: 'father',
        preferredContact: 'phone',
        language: 'french',
        status: 'active',
      });
    }
  }, [isOpen, parent, mode]);

  // Générer un ID unique pour les nouveaux parents
  useEffect(() => {
    if (mode === 'create' && !formData.parentId) {
      const year = new Date().getFullYear();
      const randomNum = Math.floor(Math.random() * 1000)
        .toString()
        .padStart(3, '0');
      setFormData((prev) => ({
        ...prev,
        parentId: `PAR${year}${randomNum}`,
      }));
      logger.debug(`🆔 [ParentFormModal] ID généré pour nouveau parent: PAR${year}${randomNum}`);
    }
  }, [mode, formData.parentId]);

  const handleInputChange = (field, value, nested = null) => {
    if (nested) {
      setFormData((prev) => ({
        ...prev,
        [nested]: {
          ...prev[nested],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Effacer l'erreur du champ modifié
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validation des champs obligatoires
    if (!formData.firstName.trim()) newErrors.firstName = 'Le prénom est obligatoire';
    if (!formData.lastName.trim()) newErrors.lastName = 'Le nom est obligatoire';
    if (!formData.phone.trim()) newErrors.phone = 'Le téléphone est obligatoire';
    if (!formData.email.trim()) newErrors.email = "L'email est obligatoire";

    // Validation du format téléphone
    const phoneRegex = /^(\+212|0)[5-7][0-9]{8}$/;
    if (formData.phone && !phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Format téléphone invalide (ex: +212 6 12 34 56 78)';
    }

    // Validation du format email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Format email invalide';
    }

    // Validation contact d'urgence (optionnel, même format que téléphone principal)
    if (
      formData.emergencyContact &&
      formData.emergencyContact.trim() &&
      !phoneRegex.test(formData.emergencyContact.replace(/\s/g, ''))
    ) {
      newErrors.emergencyContact = 'Format téléphone invalide (ex: +212 6 12 34 56 78)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Calculer l'âge si date de naissance fournie
    let age = null;
    const dateOfBirth = formData.dateOfBirth && formData.dateOfBirth.trim() ? formData.dateOfBirth : null;
    if (dateOfBirth) {
      const birthDate = new Date(dateOfBirth);
      const today = new Date();
      age = today.getFullYear() - birthDate.getFullYear();
    }

    const parentData = {
      ...formData,
      dateOfBirth,
      age,
      fullName: `${formData.firstName} ${formData.lastName}`,
      createdAt: mode === 'create' ? new Date().toISOString() : parent?.createdAt,
      updatedAt: new Date().toISOString(),
      // Mapper les champs camelCase vers snake_case pour le backend
      emergency_contact_name: formData.emergencyContactName || undefined,
      emergency_contact_phone: formData.emergencyContact || undefined,
    };

    onSubmit(parentData);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  {mode === 'create' ? 'Nouveau Parent' : 'Modifier le Parent'}
                </h2>
                <p className="text-gray-500 mt-1 ml-12">
                  {mode === 'create'
                    ? 'Renseignez les informations du parent'
                    : `Modifier les informations de ${parent?.first_name ?? parent?.firstName ?? ''} ${parent?.last_name ?? parent?.lastName ?? ''}`.trim() ||
                      'ce parent'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8 bg-gray-50/30">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-6">
                  {/* Ligne 1 : Prénom et Nom */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Prénom <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                          errors.firstName
                            ? 'border-red-300 focus:border-red-500'
                            : 'border-gray-200 focus:border-blue-500'
                        }`}
                        placeholder="Prénom du parent"
                      />
                      {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Nom <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                          errors.lastName
                            ? 'border-red-300 focus:border-red-500'
                            : 'border-gray-200 focus:border-blue-500'
                        }`}
                        placeholder="Nom de famille"
                      />
                      {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
                    </div>
                  </div>

                  {/* Ligne 2 : Email et Téléphone principal */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                          errors.email ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-blue-500'
                        }`}
                        placeholder="parent@email.com"
                      />
                      {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Téléphone principal <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                          errors.phone ? 'border-red-300 focus:border-red-500' : 'border-gray-200 focus:border-blue-500'
                        }`}
                        placeholder="+212 6 12 34 56 78"
                      />
                      {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                    </div>
                  </div>

                  {/* Ligne 3 : Contact d'urgence (nom) et Téléphone d'urgence */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Contact d'urgence</label>
                      <input
                        type="text"
                        value={formData.emergencyContactName}
                        onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                        placeholder="Nom du contact d'urgence"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Téléphone d'urgence</label>
                      <input
                        type="tel"
                        value={formData.emergencyContact}
                        onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                        className={`w-full px-4 py-2.5 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none transition-all ${
                          errors.emergencyContact
                            ? 'border-red-300 focus:border-red-500'
                            : 'border-gray-200 focus:border-blue-500'
                        }`}
                        placeholder="+212 6 12 34 56 78"
                        inputMode="tel"
                        autoComplete="tel"
                      />
                      {errors.emergencyContact && (
                        <p className="text-xs text-red-500 mt-1">{errors.emergencyContact}</p>
                      )}
                    </div>
                  </div>

                  {/* Ligne 4 : Date de naissance et Statut */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Date de naissance</label>
                      <input
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Statut</label>
                      <select
                        value={formData.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                      >
                        <option value="active">Actif</option>
                        <option value="inactive">Inactif</option>
                      </select>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="px-8 py-6 border-t border-gray-100 bg-gray-50 flex justify-end items-center gap-3 sticky bottom-0 z-10">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 text-gray-700 font-medium hover:bg-gray-100 rounded-xl transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-500/20 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-600/20"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Traitement...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>{mode === 'create' ? 'Créer le parent' : 'Enregistrer les modifications'}</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ParentFormModal;
